import Linkify from "linkify-react";

type ParsedBlocks = {
    reqBlock: string | null;
    pipBlock: string | null;
    eciBlock: string | null;
    normalText: string;
};

const extractTag = (input: string, tag: string) => {
    const startTag = `<${tag}>`;
    const endTag = `</${tag}>`;

    if (!input.startsWith(startTag)) {
        return { value: null, rest: input };
    }

    const endIndex = input.indexOf(endTag);

    if (endIndex === -1) {
        return { value: null, rest: input };
    }

    const value = input.substring(startTag.length, endIndex);
    const rest = input.substring(endIndex + endTag.length);

    return { value, rest };
};

export const parseTextBlocks = (text: string): ParsedBlocks => {
    let current = text;

    const { value: reqBlock, rest: afterReq } = extractTag(current, "req");
    current = afterReq;

    const { value: pipBlock, rest: afterPip } = extractTag(current, "pip");
    current = afterPip;

    const { value: eciBlock, rest: afterEci } = extractTag(current, "eci");
    current = afterEci;

    return {
        reqBlock,
        pipBlock,
        eciBlock,
        normalText: current
    };
};

export default function JVMessage({ text, isImage, isMine }: { text: string, isImage: boolean, isMine: boolean }) {
    return (
        <MessageContent {...parseTextBlocks(text)} isImage={isImage} isMine={isMine} />
    );
}

type Props = {
    reqBlock?: string | null;
    pipBlock?: string | null;
    eciBlock?: string | null;
    normalText: string;
    isMine?: boolean;
    isImage?: boolean;
};

const MessageContent = ({ reqBlock, pipBlock, eciBlock, normalText, isMine, isImage }: Props) => {
    return (
        <div style={getContainerStyle(isImage)}>
            <Blocks
                reqBlock={reqBlock}
                pipBlock={pipBlock}
                eciBlock={eciBlock}
            />
            <LinkifiedText text={normalText} isMine={isMine} />
        </div>
    );
};

export const TagContent = ({ reqBlock, pipBlock, eciBlock }: Props) => {
    return (
        <Blocks
            reqBlock={reqBlock}
            pipBlock={pipBlock}
            eciBlock={eciBlock}
            tagOnly={true}
        />
    );
};

const Blocks = ({ reqBlock, pipBlock, eciBlock, tagOnly }: { reqBlock?: string | null, pipBlock?: string | null, eciBlock?: string | null, tagOnly?: boolean }) => {
    const items = [
        { value: reqBlock, style: { ...styles.reqBox, marginBottom: tagOnly ? 0 : 6 }, tag: 'REQ' },
        { value: pipBlock, style: { ...styles.pipBox, marginBottom: tagOnly ? 0 : 6 }, tag: 'PIP' },
        { value: eciBlock, style: { ...styles.eciBox, marginBottom: tagOnly ? 0 : 6 }, tag: 'ECI' }
    ];

    return (
        <>
            {items.map((item, i) =>
                item.value &&
                <div key={i} style={item.style}>
                    <span style={{
                        position: 'absolute',
                        top: 0, right: 8,
                        fontSize: 6,
                    }}>{item.tag}</span>
                    {item.value}
                </div>
            )}
        </>
    );
};

const LinkifiedText = ({ text, isMine }: { text: string; isMine?: boolean; }) => {
    return (
        <Linkify options={{
            target: "_blank",
            render: ({ attributes, content }) => (
                <a {...attributes} style={getLinkStyle(isMine)}>{content}</a>
            )
        }}>{text}</Linkify>
    );
};

const getContainerStyle = (isImage?: boolean): React.CSSProperties => ({
    padding: isImage ? "8px 10px" : 0
});

const getLinkStyle = (isMine?: boolean): React.CSSProperties => ({
    color: isMine ? "rgba(255,255,255,0.9)" : "#1d9bf0",
    textDecoration: "underline",
    fontWeight: 500,
    wordBreak: "break-word"
});

const baseBox: React.CSSProperties = {
    padding: "6px 10px",
    borderRadius: 8,
    fontSize: 12,
    marginBottom: 6,
    fontWeight: 600,
    position: 'relative',
};

const styles = {
    reqBox: {
        ...baseBox,
        background: "#f1f5f9",
        color: "#334155"
    },
    pipBox: {
        ...baseBox,
        background: "#ecfeff",
        color: "#155e75"
    },
    eciBox: {
        ...baseBox,
        background: "#fef9c3",
        color: "#854d0e"
    }
};